/**
 * The OPERATIONAL ANCHOR of a related group — the package a group is counted
 * in, as distinct from the member that owns its identity.
 *
 * Three concepts the fold used to conflate into one, now separated:
 *
 *   group identity / normalized ownership
 *     The STICKY canonical member (`countGroupFold`'s `basisItemId`). It owns
 *     the count unit every stored quantity is denominated in, the count_items
 *     row a typed number lands on, and the allocation line's item id. It is
 *     deliberately stable — a recency flip must never re-key a need line or
 *     re-denominate a stored PAR.
 *
 *   physical stock target
 *     The normalized PAR / on-hand / need, in that basis unit. INVARIANT. It
 *     does not move because another vendor became cheaper, because a package
 *     rounded up to a MOQ, or because the anchor below changed.
 *
 *   operational anchor  ← this module
 *     The package the restaurant currently deals in: a BIB, a bottle, a jug,
 *     a case. It follows durable operational evidence (a verified purchase, a
 *     newly imported package, what this location was last counted in, a pin)
 *     and it may belong to ANY member of the group, including one whose own
 *     `inventory_items.unit` differs from the basis unit. It changes only how
 *     PAR / COUNT / NEED are EXPRESSED.
 *
 * Superseded (2026-08-21): "the sticky canonical always owns the group's count
 * unit", "recency may never redefine the displayed PAR unit", and "an odd-unit
 * favored member can never front the card". Those rules protected the physical
 * target by freezing its expression, which is a heavier cure than the disease:
 * a store buying Diet Coke in 5 gal BIBs was counting it in gallons because a
 * sibling item happened to be the sticky canonical. The target is now protected
 * by keeping it normalized and converting only for display.
 *
 * ONE precedence, ONE conversion engine — neither is reimplemented here:
 *
 *   precedence  `countPresentation.resolveCountPresentation`, the same
 *               pin → verified purchase → newest package → last counted here
 *               → preferred → only → canonical chain a single item uses. This
 *               module's whole job at that layer is to hand it the GROUP's
 *               pooled options and rungs instead of one member's.
 *   conversion  `packageBasis.resolveNeedToPackageBasis`, the same resolver
 *               the order math uses to size a package against a need. A
 *               cross-unit member's package is measured in the basis unit
 *               through it — confirmed interpretation, then structured
 *               columns, then the stored quantity, then the item's own
 *               physical bridge — and when it refuses, so does this module.
 *
 * Nothing here parses pack text and nothing here holds a conversion factor.
 * An unbridgeable package (6.6 lb of cherries against a gallon jar) is
 * reported as needing review, never guessed.
 */

import {
  fallbackCountLadder,
  packageAllowsFractionalCount,
  pluralNoun,
  resolveCountPresentation,
  PRESENTATION_SOURCES,
  PRESENTATION_SOURCE_TEXT,
} from './countPresentation.js';
import {
  itemCountUnitPhysicalBasis,
  resolveNeedToPackageBasis,
} from './packageBasis.js';
import { sameCountUnitName } from '../../../supabase/functions/_shared/packText.mjs';

const text = (v) => (v == null ? '' : String(v).trim());
const round6 = (n) => Math.round(n * 1e6) / 1e6;
const positive = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** What the anchor resolution concluded. */
export const ANCHOR_STATUS = Object.freeze({
  /** A group package resolved: count / PAR / need read in it. */
  PACKAGE: 'package',
  /** No package evidence anywhere in the group — the basis unit itself. */
  BASIS: 'basis',
  /**
   * The group's STRONGEST operational evidence names a package that cannot be
   * measured in the basis unit by any authoritative route (6.6 lb of cherries
   * against a gallon jar). Nothing is guessed: the anchor still carries the
   * best SAFE answer — a weaker package, or the basis unit — so counting
   * continues, and `review` says which product needs a human.
   */
  NEEDS_REVIEW: 'needs_review',
});

export const ANCHOR_REVIEW_TEXT = 'Quantity conversion needs review';

// Same ordering rule as relatedItemGroups.byIdAscending.
const idAscending = (left, right) => {
  const l = Number(left);
  const r = Number(right);
  if (Number.isFinite(l) && Number.isFinite(r) && l !== r) return l - r;
  const ls = String(left);
  const rs = String(right);
  if (ls < rs) return -1;
  if (ls > rs) return 1;
  return 0;
};

const activeOptionsOf = (optionsByItemId, itemId) =>
  (optionsByItemId?.[itemId] || optionsByItemId?.[Number(itemId)] || [])
    .filter((option) => option && text(option.status) === 'active');

/**
 * The member's own count unit, from whichever of the three sources the caller
 * carried. Mirrors the fold's `foldUnitKey` sources so the two agree about
 * which members are "odd-unit".
 */
const unitOfMember = ({ itemsByItemId, laddersByItemId, unitByItemId }, itemId) =>
  text(unitByItemId?.[itemId])
  || text(itemsByItemId?.[itemId]?.unit ?? itemsByItemId?.[itemId]?.count_unit)
  || text(laddersByItemId?.[itemId]?.countUnit)
  || '';

/**
 * How much of the BASIS unit one package of `option` holds, through the one
 * shared package resolver. Returns null when no authoritative route exists —
 * a weight package against a volume count unit with no product-specific
 * bridge, an unconfirmed package size, a fallback_default quantity.
 *
 * `optionItem` is the package's OWN item, `needUnit` is the group's basis
 * unit, and `needBridge` is the BASIS item's physical definition — the same
 * three-way split costToFulfill uses. The option's item matters because
 * extractPackageMeasurements falls back to `item.unit` when a row has no
 * `normalized_count_unit` of its own: handing it the basis item would label a
 * sibling's "1 package" as one GALLON when it is one BIB, which is precisely
 * the fabricated conversion this module exists to refuse.
 */
function packageQuantityInBasisUnit({ option, optionItem, basisUnit, needBridge }) {
  const resolved = resolveNeedToPackageBasis({
    // One package's worth. The need value is irrelevant to the ratio; a
    // positive one keeps the resolver on its ordinary path.
    needQty: 1,
    needUnit: basisUnit,
    option,
    item: optionItem,
    needBridge,
  });
  if (!resolved?.compatible) return null;
  return positive(resolved.quantityPerPackageInNeedUnit);
}

/**
 * The group's rung set, every rung expressed in the BASIS unit.
 *
 * A member counted in the basis unit contributes its ladder unchanged — the
 * server already sized those rungs in that unit, inner rungs included. A
 * member counted in something else contributes one rung per option, sized
 * through `resolveNeedToPackageBasis`; its inner rungs are deliberately not
 * synthesized, because "half of a package we had to bridge" is a second
 * derivation and the group has a better rung for it anyway.
 */
function buildGroupRungs({
  members, basisItemId, basisUnit, needBridge,
  optionsByItemId, laddersByItemId, itemsByItemId, unitByItemId,
}) {
  const rungs = [];
  const options = [];
  const unresolved = [];
  const unbridged = [];
  const seenOptionIds = new Set();
  const ctx = { itemsByItemId, laddersByItemId, unitByItemId };

  members.forEach((itemId) => {
    const memberUnit = unitOfMember(ctx, itemId);
    const memberOptions = activeOptionsOf(optionsByItemId, itemId);
    if (!memberOptions.length) return;
    // A member with no unit on record is treated as sharing the basis unit —
    // the same reading foldUnitKey gives an empty unit, and the ladder the
    // server built for it is already in that unit.
    const sameUnit = !memberUnit || sameCountUnitName(memberUnit, basisUnit)
      || memberUnit.toLowerCase() === text(basisUnit).toLowerCase();

    memberOptions.forEach((option) => {
      const optionId = option.id ?? null;
      if (optionId == null || seenOptionIds.has(String(optionId))) return;
      seenOptionIds.add(String(optionId));
      options.push(option);
    });

    if (sameUnit) {
      const ladder = laddersByItemId?.[itemId]
        || fallbackCountLadder({ countUnit: memberUnit || basisUnit, options: memberOptions });
      (ladder.rungs || []).forEach((rung) => {
        if (rung.level === 'canonical') return;
        if (!positive(rung.qtyInCountUnit)) return;
        rungs.push({ ...rung, memberItemId: String(itemId), bridge: null });
      });
      (ladder.unresolvedOptions || []).forEach((entry) => unresolved.push(entry));
      return;
    }

    // Cross-unit member: its ladder is denominated in ITS unit and cannot be
    // pooled as-is. Re-measure each package in the basis unit instead.
    const optionItem = { ...(itemsByItemId?.[itemId] || {}), id: itemId, unit: memberUnit };
    memberOptions.forEach((option) => {
      const qty = packageQuantityInBasisUnit({ option, optionItem, basisUnit, needBridge });
      const vendorLabel = text(option.vendor_name_text) || null;
      const orderUnit = text(option.purchase_unit) || text(memberUnit) || 'package';
      if (qty === null) {
        const entry = {
          optionId: option.id ?? null,
          memberItemId: String(itemId),
          vendorLabel,
          unitLabel: orderUnit,
          packDescription: text(option.pack_description) || null,
          reason: ANCHOR_REVIEW_TEXT,
        };
        unbridged.push(entry);
        // Also an UNRESOLVED count format, so the existing "needs package
        // review" affordance lists it with its reason instead of the package
        // simply not being offered. A user who bought from that vendor has
        // to be able to see why they cannot count in it.
        unresolved.push(entry);
        return;
      }
      rungs.push({
        key: `${option.id}:package`,
        level: 'package',
        optionId: option.id ?? null,
        entryKind: 'full_package',
        label: vendorLabel ? `${vendorLabel} — ${orderUnit}` : orderUnit,
        unitLabel: orderUnit,
        detail: text(option.pack_description) || null,
        qtyInCountUnit: round6(qty),
        // Divisibility is judged in the unit the rung is now expressed in —
        // one 5 gal BIB against a gallon-counted shelf is divisible, and
        // reading it as whole packages floors "0.1 BIB" to nothing.
        allowsFractional: packageAllowsFractionalCount({ option, countUnit: basisUnit }),
        memberItemId: String(itemId),
        // Recorded so a surface can say WHERE the number came from; the
        // conversion itself was done by the shared resolver, not here.
        bridge: { fromUnit: memberUnit || orderUnit, toUnit: basisUnit },
      });
    });
  });

  // Exactly one canonical rung, in the basis unit — the count field's
  // fallback meaning, and the resolver's last resort.
  const basisLadder = laddersByItemId?.[basisItemId]
    || fallbackCountLadder({ countUnit: basisUnit, options: activeOptionsOf(optionsByItemId, basisItemId) });
  const canonical = (basisLadder.rungs || []).find((rung) => rung.level === 'canonical')
    || (basisUnit
      ? {
        key: 'canonical',
        level: 'canonical',
        optionId: null,
        entryKind: 'canonical_measure',
        label: basisUnit,
        unitLabel: basisUnit,
        detail: null,
        qtyInCountUnit: 1,
        allowsFractional: true,
      }
      : null);
  if (canonical) rungs.push({ ...canonical, memberItemId: String(basisItemId), bridge: null });

  return { rungs, options, unresolved, unbridged };
}

/**
 * The package a related group is currently counted in.
 *
 * @param {object} args
 * @param {*} args.basisItemId            the group's stock-basis member
 * @param {string} args.basisUnit         its count unit — every stored quantity is in it
 * @param {object} [args.basisRow]        the count row carrying pins / last-used memory
 * @param {Array} [args.memberItemIds]    every PRESENT member, odd-unit ones included
 * @param {object} [args.optionsByItemId] item id → purchase_options rows
 * @param {object} [args.laddersByItemId] item id → server ladder (optional; a
 *                                        member without one falls back to the
 *                                        option-row ladder, same as counting)
 * @param {object} [args.itemsByItemId]   item id → inventory item (for units + the bridge)
 * @param {object} [args.unitByItemId]    item id → count unit, when the caller has it
 * @returns {{ status, optionId, memberItemId, unitLabel, qtyInBasisUnit,
 *             basisUnit, rung, rungs, source, sourceText, equivalenceText,
 *             review, bridged }}
 */
export function resolveGroupCountAnchor({
  basisItemId,
  basisUnit,
  basisRow = null,
  memberItemIds = [],
  optionsByItemId = {},
  laddersByItemId = null,
  itemsByItemId = {},
  unitByItemId = null,
} = {}) {
  const basisKey = basisItemId == null ? null : String(basisItemId);
  const unit = text(basisUnit) || null;
  const inBasis = {
    status: ANCHOR_STATUS.BASIS,
    optionId: null,
    memberItemId: basisKey,
    unitLabel: unit,
    qtyInBasisUnit: 1,
    basisUnit: unit,
    rung: null,
    rungs: [],
    source: PRESENTATION_SOURCES.CANONICAL,
    sourceText: PRESENTATION_SOURCE_TEXT[PRESENTATION_SOURCES.CANONICAL],
    equivalenceText: null,
    review: null,
    bridged: false,
    presentation: null,
  };
  if (!basisKey || !unit) return inBasis;

  const present = [...new Set([basisKey, ...(memberItemIds || []).map(String)])]
    .filter((id) => id != null && id !== '');
  // Own-first, then ascending member id — the same stable order the pooled
  // option lists use, so the resolver's tiebreaks are reproducible.
  const members = [
    basisKey,
    ...present.filter((id) => id !== basisKey).sort(idAscending),
  ];

  const basisItem = itemsByItemId?.[basisKey]
    ? { ...itemsByItemId[basisKey], unit }
    : { id: basisItemId, unit };
  // What one BASIS count unit physically holds, from the basis member's OWN
  // options. A sibling's package may never define this item's unit — that is
  // the rule itemCountUnitPhysicalBasis already enforces, and it is why a
  // bottle-counted item can measure a 5 gal jug while a case-counted one
  // cannot invent a case size from somebody else's case.
  const needBridge = itemCountUnitPhysicalBasis({
    item: basisItem,
    options: activeOptionsOf(optionsByItemId, basisKey),
  });

  const built = buildGroupRungs({
    members,
    basisItemId: basisKey,
    basisUnit: unit,
    needBridge,
    optionsByItemId,
    laddersByItemId,
    itemsByItemId,
    unitByItemId,
  });

  const presentation = resolveCountPresentation({
    row: basisRow,
    options: built.options,
    rungs: built.rungs,
    unresolved: built.unresolved,
  });

  const rung = presentation?.rung ?? null;
  const per = positive(rung?.qtyInCountUnit);

  // Checked on EVERY path, not only when the fallback is the bare count unit.
  // A group whose newest verified purchase cannot be measured here still has
  // to say so, even when an older package quietly resolved instead — a silent
  // demotion to a weaker package is how a conversion question disappears.
  const review = wouldBeAnchorReview({ basisRow, built, unit });

  if (!rung || rung.level === 'canonical' || per === null) {
    return {
      ...inBasis,
      rungs: built.rungs,
      review,
      status: review ? ANCHOR_STATUS.NEEDS_REVIEW : ANCHOR_STATUS.BASIS,
      presentation,
    };
  }

  return {
    status: review ? ANCHOR_STATUS.NEEDS_REVIEW : ANCHOR_STATUS.PACKAGE,
    optionId: rung.optionId ?? null,
    memberItemId: rung.memberItemId ?? basisKey,
    unitLabel: text(rung.unitLabel) || unit,
    qtyInBasisUnit: per,
    basisUnit: unit,
    rung,
    rungs: built.rungs,
    source: presentation.source,
    sourceText: presentation.defaultReason || presentation.sourceText || null,
    equivalenceText: describeAnchorEquivalence({ unitLabel: rung.unitLabel, qtyInBasisUnit: per, basisUnit: unit }),
    review,
    bridged: !!rung.bridge,
    // The full presentation this anchor was resolved from — the same object
    // shape a single item's count card uses, so the "Count as" menu, the
    // selector gate and the default-reason wording all read the GROUP's
    // pooled choices without a second resolver call.
    presentation,
  };
}

/**
 * Would an unbridgeable package have won, had it been measurable?
 *
 * Re-runs the SAME precedence over a rung set in which every unbridged option
 * is present at an arbitrary size. The size is never used — only the identity
 * of the winner is — so no fabricated quantity can escape this function.
 */
function wouldBeAnchorReview({ basisRow, built, unit }) {
  if (!built.unbridged.length) return null;
  const hypothetical = [
    ...built.rungs,
    ...built.unbridged.map((entry) => ({
      key: `${entry.optionId}:package`,
      level: 'package',
      optionId: entry.optionId,
      entryKind: 'full_package',
      label: entry.vendorLabel ? `${entry.vendorLabel} — ${entry.unitLabel}` : entry.unitLabel,
      unitLabel: entry.unitLabel,
      detail: entry.packDescription,
      // Placeholder: discarded with the result. Only the WINNER's identity
      // is read below.
      qtyInCountUnit: 1,
      allowsFractional: false,
      memberItemId: entry.memberItemId,
    })),
  ];
  const would = resolveCountPresentation({
    row: basisRow,
    options: built.options,
    rungs: hypothetical,
    unresolved: built.unresolved,
  });
  const winnerId = would?.rung?.optionId ?? null;
  const match = built.unbridged.find((entry) => String(entry.optionId) === String(winnerId));
  if (!match) return null;
  return {
    reason: ANCHOR_REVIEW_TEXT,
    optionId: match.optionId,
    memberItemId: match.memberItemId,
    unitLabel: match.unitLabel,
    vendorLabel: match.vendorLabel,
    packDescription: match.packDescription,
    basisUnit: unit,
  };
}

/** "1 BIB = 5 gal" / "1 case = 12 bottles" — the sentence that makes the
 *  anchor auditable against the package in somebody's hands. */
export function describeAnchorEquivalence({ unitLabel, qtyInBasisUnit, basisUnit } = {}) {
  const per = positive(qtyInBasisUnit);
  const label = text(unitLabel);
  const unit = text(basisUnit);
  if (!label || !unit || per === null) return null;
  if (per === 1 && sameCountUnitName(label, unit)) return null;
  const rounded = round6(per);
  return `1 ${label} = ${rounded} ${pluralNoun(unit, rounded)}`;
}
